/** @jsx jsx */
import { jsx } from '@emotion/react';
import { daysFromNow } from '@zapier/date';
import { Alert, Link } from '@zapier/design-system';
import { Price } from '@zapier/currency';
import { Fragment } from 'react';
import type { ResponseData } from '../useAlertBanner/useAlertBannerData';
import { useUniversalNavContext } from '../../../../universal-nav/hooks';
import { UserInteractionEvent } from '../../../../universal-nav/utils/avroEvents';
import { BannerProps } from './types';
import { getProfessionalPlanData } from './utils';

export const shouldRender = (data: ResponseData) => {
  const { currentAccount } = data;
  const { isTrialing, trialEndDate } = currentAccount;
  return (
    !isTrialing &&
    !!trialEndDate &&
    currentAccount.plan.amount === 0 &&
    daysFromNow(trialEndDate) <= 14
  );
};

export const Component = function ProfessionalTrialEnded(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const { data, currency } = props;

  const { professionalUpgradeUrl, professionalPlanAmount } =
    getProfessionalPlanData(data.possiblePlans);
  const nextPlanAmount = professionalPlanAmount;
  const upgradeUrl = professionalUpgradeUrl;

  const pricingText = nextPlanAmount > 0 && (
    <span>
      for as low as <Price currency={currency}>{nextPlanAmount}</Price>
      /month
    </span>
  );

  const onUpgrade = () => {
    props.trackEvent(UserInteractionEvent, {
      interaction_name: 'professional_trial_ended_banner_cta_clicked',
      interaction_goal: 'upgrade_plan',
      event_action: 'click',
      option_selected: 'not_applicable',
    });
  };

  return (
    <Alert
      layout={props.layout}
      title="Your Professional trial has ended"
      type="info"
    >
      <Fragment>
        <Link href={getFullyQualifiedURL(upgradeUrl)} onClick={onUpgrade}>
          Upgrade
        </Link>
        {/* Use `&nbsp;` to prevent text wrapping from making "Upgrade" a widow/orphan */}
        &nbsp;{pricingText} to keep using multi-step Zaps, filters, premium
        apps, and more
      </Fragment>
    </Alert>
  );
};
